import React, { memo, useEffect } from "react";
import { User, UserReport } from "../../types";
import {
  Card,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  styled,
  Typography,
} from '@material-ui/core';

import IconArrow from '@material-ui/icons/ArrowRightAlt';

import { useQueryParams, StringParam, withDefault } from "use-query-params";
import { actions } from '../../services/userReports';
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store";


import { areEqual, FixedSizeList, ListChildComponentProps } from "react-window";
import { ReactWindowScroller } from "../../components/WindowScroller";
import { Box } from "@material-ui/system";
import { ProfileAvatar } from "../../components/UserPhoto/UserPhoto";
import timeAgo from "../../utils/timeAgo";

const ReportItem: React.FC<ListChildComponentProps<UserReport[]>> = ({ data, index, style }) => {
  const report = data[ index ];

  return (
    <Box style={style} sx={{ pb: 1 }}>
      <Stack direction="row" justifyContent="center">
        <Card variant="outlined" sx={{ padding: 1, width: '100%', maxWidth: 600, }}>
          <Stack direction="column" spacing={0} alignItems="center">
            <Stack direction="row" spacing={1} justifyContent="space-around" alignItems="center" sx={{ width: '100%' }}>
              <ProfileAvatar userId={report.fromUserId} size={75} />
              <IconArrow fontSize="large" />
              <ProfileAvatar userId={report.userId} size={100} />
            </Stack>
            <Typography variant="caption">
              Status: {report.status}
            </Typography>
            <Typography variant="caption">
              Reason: {report.reason}
            </Typography>
            <Typography variant="caption">
              Created: {timeAgo(report.createTime.toDate())}
            </Typography>
          </Stack>
        </Card>
      </Stack>
    </Box>
  );

};

const ReportItemMemo = memo(ReportItem, areEqual);


const Root = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  position: 'relative',
  flex: 1,
});

interface Props {
  user: User;
}

const UserReports: React.FC<Props> = ({ user }) => {
  const dispatch = useDispatch();
  const state = useSelector((state: RootState) => state.userReports);

  const [ filters, setFilters ] = useQueryParams({
    who: withDefault(StringParam, 'all'),
  });

  useEffect(() => {
    dispatch(actions.loadReports(user.id));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ user.id ]);

  const handleFilterChange = (event: SelectChangeEvent<string>) => {
    setFilters({
      [ event.target.name ]: event.target.value
    }, 'replaceIn');
  };

  if (state.loading || !state.reports[ user.id ]) {
    return (
      <Root>
        <CircularProgress />
      </Root>
    );
  }


  let list: UserReport[] = [ ...state.reports[ user.id ] ];

  if (filters.who === 'fromMe') {
    list = state.reports[ user.id ].filter(item => item.fromUserId === user.id);
  }
  else if (filters.who === 'toMe') {
    list = state.reports[ user.id ].filter(item => item.userId === user.id);
  }

  return (
    <Root>
      <Stack direction="column" spacing={1} alignItems="flex-start" sx={{ width: '100%' }}>
        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel id="who-label">From Who</InputLabel>
          <Select
            name="who"
            labelId="type-label"
            value={filters.who || 'all'}
            label="From Who"
            onChange={handleFilterChange}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="fromMe">From me</MenuItem>
            <MenuItem value="toMe">To Me</MenuItem>
          </Select>
        </FormControl>
        <Divider flexItem />

        <ReactWindowScroller>
          {({ ref, outerRef, style, onScroll }) => (
            <FixedSizeList
              ref={ref}
              outerRef={outerRef}
              style={style}
              onScroll={onScroll}
              height={window.innerHeight}
              width="100%"
              itemCount={list.length}
              itemSize={190}
              itemData={list}
            >
              {ReportItemMemo}
            </FixedSizeList>
          )}
        </ReactWindowScroller>

      </Stack>
    </Root>
  );
};

export default UserReports;